import { DocumentNavigationSidebarContainer } from 'features/libra/components/DocumentNavigationSidebarContainer'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { FC } from 'react'
import styled from 'styled-components'
import { GeckoComponent, RolodexEntryType } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid
    ${({ theme }) => theme.forecast.border ?? theme.forecast.border2};
`

const Empty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  text-align: center;
`

/**
 * Workspace panel that hosts the document navigation sidebar with upload UI.
 * Resolves `unitId` from the ticket's tags via `useTicketAccess`. Document
 * data + UI state come from `DocumentDataProvider`/`DocumentUIProvider`
 * mounted by the workspace container.
 */
export const Component: FC = () => {
  const { ticketTags } = useTicketAccess()
  const ticketUnitId = getStringTag(ticketTags, RolodexEntryType.UNIT)
  const componentId = getStringTag(ticketTags, RolodexEntryType.COMPONENT)

  // Fall back to the component-derived unit when the ticket itself lacks a
  // UNIT tag -- FFS tickets created via certain paths bind COMPONENT only,
  // and the component entry carries the unit tag.
  const { entries: componentEntries, isLoading: componentLoading } =
    useRolodexEntriesWithSyncedRefreshes(
      !ticketUnitId && componentId
        ? { type: RolodexEntryType.COMPONENT, ids: [componentId] }
        : undefined
    )
  const fallbackComponent = componentEntries[0] as GeckoComponent | undefined
  const derivedUnitId =
    ticketUnitId ??
    (fallbackComponent
      ? getStringTag(fallbackComponent.tags, RolodexEntryType.UNIT)
      : undefined)

  if (!derivedUnitId) {
    // While the fallback component query is loading, don't surface the
    // "missing tag" error -- the unit might still resolve from the
    // component entry.
    if (componentLoading) {
      return (
        <Container>
          <Empty>Loading...</Empty>
        </Container>
      )
    }
    return (
      <Container>
        <Empty>Ticket is missing a unit tag.</Empty>
      </Container>
    )
  }

  return (
    <Container>
      <DocumentNavigationSidebarContainer unitId={derivedUnitId} showUploader />
    </Container>
  )
}
